/**
 * App / 小程序 / H5 宿主交互
 * 小程序 token 入场也会把 isApp 置 true，分发时必须先判小程序
 */
import { onBeforeUnmount, onMounted } from 'vue'
import { getToken } from '@/utils'
import { syncAppContextFromPayload } from '@/router/external-entry'
import {
  isMiniProgram,
  markMiniProgramListDirty,
  navigateBackMiniProgram,
  setupMiniProgramListRefresh,
} from '@/utils/miniprogram'

/** @returns {'miniprogram' | 'app' | 'h5'} */
export function getHostKind() {
  if (isMiniProgram()) return 'miniprogram'
  if (window.$store?.isApp) return 'app'
  return 'h5'
}

/** 仅浏览器 H5 展示自绘顶栏，App / 小程序用原生顶栏 */
export function showH5Nav() {
  return getHostKind() === 'h5'
}

/** 通道号：缺省或非法时为 1 */
export function normalizeChannelNumber(value) {
  return Math.max(1, Math.floor(Number(value) || 1))
}

/**
 * 另一 web-view / 原生页共用的上下文
 * @param {Record<string, string>} [extra]
 */
export function buildHostContext(extra = {}) {
  const project = window.$store?.currentProject || {}
  const userInfo = window.$store?.userInfo || {}
  return {
    project_id: String(project.id || ''),
    projectName: String(project.name || ''),
    app_token: getToken(),
    userRole: String(userInfo.sys_role || ''),
    ...extra,
  }
}

/**
 * 按宿主打开页面。小程序优先，避免 isApp 同时为 true 时误走 App Bridge
 * @param {{ mp?: () => void, app?: () => void, h5?: () => void }} handlers
 */
export function openInHost({ mp, app, h5 } = {}) {
  const kind = getHostKind()
  if (kind === 'miniprogram') return mp?.()
  if (kind === 'app') return app?.()
  return h5?.()
}

/** 详情内操作成功后通知对应列表刷新。scope：alarm | sensor */
export function markHostListDirty(scope) {
  const kind = getHostKind()
  if (kind === 'miniprogram') {
    markMiniProgramListDirty(scope)
    return
  }
  if (kind === 'app') {
    window.AIOTBridge?.markListDirty?.()
  }
}

/**
 * 关闭当前容器。H5 执行传入的返回
 * @param {{ h5?: () => void }} [options]
 */
export function closeHostPage({ h5 } = {}) {
  const kind = getHostKind()
  if (kind === 'miniprogram') {
    navigateBackMiniProgram()
    return
  }
  if (kind === 'app') {
    window.AIOTBridge?.closeDetail?.()
    return
  }
  h5?.()
}

/**
 * 列表页：App 事件刷新 + 小程序脏标记刷新
 * 须在 setup 内同步调用
 * @param {string} eventName
 * @param {() => void} reload
 * @param {'alarm' | 'sensor'} scope
 */
export function useHostListRefresh(eventName, reload, scope) {
  const onHostRefresh = (event) => {
    syncAppContextFromPayload(event?.detail)
    reload?.()
  }

  let disposeMpListRefresh = null

  onMounted(() => {
    window.addEventListener(eventName, onHostRefresh)
    disposeMpListRefresh = setupMiniProgramListRefresh(() => reload?.(), scope)
  })

  onBeforeUnmount(() => {
    window.removeEventListener(eventName, onHostRefresh)
    disposeMpListRefresh?.()
    disposeMpListRefresh = null
  })
}
